// The Findings time strip
// (~/Desktop/notes/design-prefs/fe-round1/findings-time.md): agent work over a
// frame twice the selected range, in calendar units fine enough to draw it as
// one continuous line, topped at three times its busy level so a burst runs
// off the top rather than flatten the rest; hung below its baseline, on their
// own scale, the cases in each unit by severity (one case is never lost, and a
// flood fills the lane while the rest keep their proportion to it); where the
// list's read stopped, the range hatched rather than shown clean; and the
// selection, which is the page's date range. Drag
// across it to set the range, drag an edge to change it, click a unit for its
// day; the picker in the console's header sets a preset, days or minutes.
// Hovering says a unit in words, with its most severe case.
//
// It leads the list's scroll: the line of words sticks at the top, and the
// chart scrolls up under it like any of the list's rows. Once the chart has
// gone under, a miniature of it fades in beside the words: the work as one
// line, the range shaded, a tick for each unit with cases; clicking it
// scrolls back to the chart.
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useElementSize } from "./kernelPrimitives/shared.jsx";
import { rangeSpan } from "../lib/shell.js";
import {
  SEVERITIES, ceilingOf, dayKey, frameFor, rangeOf, readSessionsByHour, riskByUnit, selectionOf, sessionsByUnit, unitHoursFor, unitStarts, wordsOf,
} from "../lib/findingsTime.js";

const NUM = new Intl.NumberFormat();
const ON_DATE = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
// Agent work above the baseline; the cases hang in a lane below it.
const WORK_H = 80;
const RISK_H = 24;
const LANE = WORK_H + RISK_H;
const AXIS_H = 20;
const EDGE = 6;
// The least height a unit's cases hang at, so one case is never lost; until a
// unit holds more than RISK_H / RISK_MIN cases, each case is that tall.
const RISK_MIN = 4;
const SEVERITY = { critical: "var(--severity-critical)", high: "var(--severity-high)", medium: "var(--severity-medium)", low: "var(--severity-low)" };
const plural = (n, one) => `${NUM.format(n)} ${one}${n === 1 ? "" : "s"}`;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Sessions by hour, read once per frame and kept while the page is loaded.
// `fixture` (the harness's recorded month) holds them by "YYYY-MM-DDTHH".
const reads = new Map();
function useHourly(days, fixture) {
  const key = `${days[0]}:${days.length}`;
  const [, bump] = useState(0);
  useEffect(() => {
    if (fixture || reads.has(key)) return;
    reads.set(key, { status: "loading" });
    readSessionsByHour(days)
      .then((value) => reads.set(key, { status: "done", value }), () => reads.set(key, { status: "error" }))
      .then(() => bump((n) => n + 1));
  }, [key]);
  const fixed = useMemo(() => {
    if (!fixture) return null;
    const out = [];
    for (const d of days) {
      for (let h = 0; h < 24; h += 1) {
        const t = new Date(d);
        t.setHours(h, 0, 0, 0);
        const n = fixture[`${dayKey(d)}T${String(h).padStart(2, "0")}`];
        if (n) out.push([t.getTime(), n]);
      }
    }
    return { status: "done", value: out };
  }, [fixture, days]);
  return fixed || reads.get(key) || { status: "loading" };
}

// A placeholder for a count still being read.
const PENDING = "inline-block h-[9px] animate-pulse rounded-[2px] bg-[color:var(--line-soft)] motion-reduce:animate-none";

// The line above the strip: the range, or the unit hovered, as its time,
// sessions and cases, then the unit's most severe case by name.
// `reading`: the cases are still being read.
function Words({ words, sessions, top, outside, reading }) {
  const n = words.cases;
  return (
    <span className="flex min-w-0 items-baseline gap-x-[14px]">
      <span className="shrink-0 text-[13px] font-medium text-[color:var(--ink)]">{words.label}</span>
      {sessions.status === "error" ? <span className="shrink-0 text-[12.5px] text-[color:var(--muted)]">Traces could not be read</span>
        : sessions.status === "loading" ? <span aria-hidden="true" className={`${PENDING} w-[72px]`} />
          : words.sessions == null ? null : <span className="fade-in shrink-0 text-[12.5px] tabular-nums text-[color:var(--muted)]">{plural(words.sessions, "trace")}</span>}
      {outside ? null : reading ? <span aria-hidden="true" className={`${PENDING} w-[56px]`} /> : (
        <span className="shrink-0 text-[12.5px] tabular-nums text-[color:var(--ink)]">
          {n ? `${NUM.format(n)}${words.unread ? "+" : ""} case${n === 1 && !words.unread ? "" : "s"}` : words.unread ? "Cases not read" : "No cases"}
        </span>
      )}
      {top ? <span className="min-w-0 truncate text-[12.5px] text-[color:var(--ink)]">{top.title}</span> : null}
    </span>
  );
}

// The sticky line's height; the list's own sticky headings sit under it.
export const STRIP_BAR_H = 40;
const MINI_H = 22;
const MINI_BASE = 15;

// The chart in miniature, for the sticky line once the chart has scrolled
// under it. `visible` fades it in; clicking it brings the chart back.
function Mini({ series, ceiling, risk, selUnits, visible, onBack }) {
  const ref = useRef(null);
  const w = Math.floor(useElementSize(ref).width);
  const n = series?.length || 0;
  const uw = n ? w / n : 0;
  let line = "";
  if (w && n) {
    const y = (i) => MINI_BASE - Math.min(MINI_BASE - 2, Math.round((series[i] / (ceiling || 1)) * (MINI_BASE - 4)));
    line = `M0 ${MINI_BASE}`;
    for (let i = 0; i < n; i += 1) line += ` L${((i + 0.5) * uw).toFixed(1)} ${y(i)}`;
    line += ` L${w} ${MINI_BASE} Z`;
  }
  return (
    <button
      ref={ref}
      type="button"
      onClick={onBack}
      tabIndex={visible ? 0 : -1}
      aria-hidden={visible ? undefined : "true"}
      title="Back to the whole timeline"
      className={`block h-[22px] w-[240px] shrink-0 cursor-pointer rounded-[7px] border-0 bg-transparent p-0 transition-opacity duration-200 motion-reduce:transition-none max-[720px]:hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--green)] ${visible ? "opacity-100" : "pointer-events-none opacity-0"}`}
      data-findings-time-mini={visible ? "shown" : "hidden"}
    >
      {line ? (
        <svg width={w} height={MINI_H} className="block" aria-hidden="true">
          <rect x={selUnits[0] * uw} y={0} width={(selUnits[1] - selUnits[0] + 1) * uw} height={MINI_H} rx={2} style={{ fill: "var(--green-soft)" }} />
          <path d={line} style={{ fill: "var(--muted-soft)", fillOpacity: 0.16, stroke: "var(--muted-soft)", strokeOpacity: 0.7, strokeWidth: 1, strokeLinejoin: "round" }} />
          <line x1={0} x2={w} y1={MINI_BASE + 0.5} y2={MINI_BASE + 0.5} style={{ stroke: "var(--line-strong)" }} />
          {risk.map((unit, i) => {
            const sev = SEVERITIES.find((k) => unit.bySeverity[k]);
            return sev ? <rect key={i} x={i * uw} y={MINI_BASE + 3} width={Math.max(2, uw)} height={4} style={{ fill: SEVERITY[sev] }} /> : null;
          })}
        </svg>
      ) : null}
    </button>
  );
}

export default function FindingsTime({ entries, range, onRange, onOpenCase, onLight, minimized = false, reading = false, readFrom = null, fixture = null }) {
  const days = useMemo(() => frameFor(range), [range]);
  const hourly = useHourly(days, fixture);
  const boxRef = useRef(null);
  const svgRef = useRef(null);
  const drag = useRef(null);
  const width = Math.floor(useElementSize(boxRef).width);
  const ids = useId().replace(/:/g, "");
  const [draft, setDraft] = useState(null);
  const [hover, setHover] = useState(null);
  const [atEdge, setAtEdge] = useState(false);
  useEffect(() => { setHover(null); }, [days]);
  // Whether the chart has scrolled (mostly) under the sticky line, measured
  // against the list's scroll area where there is one.
  const chartRef = useRef(null);
  const [under, setUnder] = useState(false);
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || typeof IntersectionObserver === "undefined") return undefined;
    const root = chart.closest("[data-overlayscrollbars-viewport]");
    const watch = new IntersectionObserver(([e]) => setUnder(e.intersectionRatio < 0.35), { root, rootMargin: `-${STRIP_BAR_H}px 0px 0px 0px`, threshold: [0, 0.35, 1] });
    watch.observe(chart);
    return () => watch.disconnect();
  }, []);
  // Back to the chart: whatever holds it scrolled, scrolled to its top.
  const back = () => {
    for (let n = chartRef.current?.parentElement; n; n = n.parentElement) {
      if (n.scrollTop > 0) { n.scrollTo?.({ top: 0, behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" }); return; }
    }
  };

  const hours = unitHoursFor(days.length, width || 1200);
  const per = 24 / hours;
  const starts = useMemo(() => unitStarts(days, hours), [days, hours]);
  const series = useMemo(() => (hourly.value ? sessionsByUnit(hourly.value, days, hours) : null), [hourly.value, days, hours]);
  const risk = useMemo(() => riskByUnit(reading ? [] : entries, days, hours), [entries, days, hours, reading]);
  // The units the range covers as drawn, which for a range to the hour or the
  // minute are those its moments fall in; dragging and nudging move its days.
  const span = useMemo(() => rangeSpan(range), [range]);
  const covered = useMemo(() => selectionOf(range, days, hours), [range, days, hours]);
  const sel = [Math.floor(covered[0] / per), Math.floor(covered[1] / per)];
  const selUnits = draft ? [Math.min(...draft) * per, (Math.max(...draft) + 1) * per - 1] : covered;
  const uw = width / starts.length;
  const ceiling = useMemo(() => ceilingOf(series), [series]);
  // A burst past the ceiling runs to the top, through the headroom.
  const heightOf = (i) => (!series ? 0 : series[i] > ceiling ? WORK_H : Math.max(series[i] ? 1 : 0, Math.round((series[i] / ceiling) * (WORK_H - 8))));
  const mostCases = Math.max(RISK_H / RISK_MIN, ...risk.map((u) => u.cases.length));
  // Units of the range before the list's read reached back (`readFrom`).
  const firstRead = readFrom == null ? 0 : (() => { const k = starts.findIndex((st) => st > readFrom); return k < 0 ? starts.length : Math.max(0, k - 1); })();
  const unreadEnd = Math.min(selUnits[1] + 1, firstRead);

  const words = useMemo(() => (hover == null ? wordsOf(selUnits[0], selUnits[1], starts, hours, series, risk, readFrom, draft ? null : span) : wordsOf(hover, hover, starts, hours, series, risk, readFrom)),
    [hover, selUnits[0], selUnits[1], starts, hours, series, risk, readFrom, draft, span]);
  // The list reads only the range, so a unit outside it has no cases to say.
  const outside = hover != null && (hover < selUnits[0] || hover > selUnits[1]);
  const top = hover != null ? risk[hover]?.cases[0] || null : null;
  useEffect(() => { onLight?.(top ? top.id : null); }, [top]);

  const unitAt = (x) => clamp(Math.floor(x / uw), 0, starts.length - 1);
  const dayOfUnit = (u) => Math.floor(u / per);
  const xOf = (e) => e.clientX - svgRef.current.getBoundingClientRect().left;

  const onPointerDown = (e) => {
    if (e.button !== 0 || !width) return;
    const x = xOf(e);
    const x0 = covered[0] * uw;
    const x1 = (covered[1] + 1) * uw;
    const mode = Math.abs(x - x0) <= EDGE ? "start" : Math.abs(x - x1) <= EDGE ? "end" : "new";
    drag.current = { mode, anchor: dayOfUnit(unitAt(x)), unit: unitAt(x), x, moved: false };
    svgRef.current.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!width) return;
    const x = xOf(e);
    const u = unitAt(x);
    const d = drag.current;
    if (!d) {
      setHover(u);
      setAtEdge(Math.abs(x - covered[0] * uw) <= EDGE || Math.abs(x - (covered[1] + 1) * uw) <= EDGE);
      return;
    }
    if (Math.abs(x - d.x) > 3) d.moved = true;
    if (!d.moved) return;
    const day = dayOfUnit(u);
    setHover(u);
    setDraft(d.mode === "start" ? [day, sel[1]] : d.mode === "end" ? [sel[0], day] : [d.anchor, day]);
  };
  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (d.moved && draft) {
      onRange(rangeOf(draft[0], draft[1], days));
      setDraft(null);
      return;
    }
    // A unit holding one case opens it; otherwise its day becomes the range.
    const held = risk[d.unit]?.cases || [];
    if (held.length === 1) onOpenCase(held[0]);
    else onRange(rangeOf(d.anchor, d.anchor, days));
  };
  const onPointerLeave = () => {
    if (drag.current) return;
    setHover(null);
  };
  const nudge = (edge) => (e) => {
    const by = { ArrowLeft: -1, ArrowRight: 1, Home: -days.length, End: days.length }[e.key];
    if (!by) return;
    e.preventDefault();
    const n = e.shiftKey && Math.abs(by) === 1 ? by * 7 : by;
    const next = edge === "start" ? [clamp(sel[0] + n, 0, sel[1]), sel[1]] : [sel[0], clamp(sel[1] + n, sel[0], days.length - 1)];
    onRange(rangeOf(next[0], next[1], days));
  };

  // The line of agent work, unit by unit, as one stepped outline.
  const outline = useMemo(() => {
    if (!series || !width) return "";
    let d = `M0 ${WORK_H}`;
    for (let i = 0; i < series.length; i += 1) {
      const y = WORK_H - heightOf(i);
      d += ` L${(i * uw).toFixed(2)} ${y} L${((i + 1) * uw).toFixed(2)} ${y}`;
    }
    return `${d} L${width} ${WORK_H} Z`;
  }, [series, width, uw, ceiling]);

  // Dates below the strip: every day where there is room, else every Monday;
  // the last day says Today.
  const dayW = uw * per;
  const isToday = dayKey(days[days.length - 1]) === dayKey(Date.now());
  const labels = width ? days.map((d, i) => [d, i]).filter(([d, i]) => (dayW >= 46 ? true : new Date(d).getDay() === 1) && (!isToday || (i + 1) * dayW < width - 44)) : [];
  const sx0 = selUnits[0] * uw;
  const sx1 = (selUnits[1] + 1) * uw;
  const corner = 8;

  return (
    <>
      {/* Sticks at the top of the list; the chart scrolls up under it. */}
      <div className={`sticky top-0 z-[3] flex h-[40px] items-center gap-[16px] border-b bg-[color:var(--bg)] transition-colors duration-150 ${under ? "border-[color:var(--line)]" : "border-transparent"}`} data-findings-time-bar="">
        <span className="flex min-w-0 flex-1 items-baseline gap-x-[14px]">
          <span aria-live="polite" className="min-w-0"><Words words={words} sessions={hourly} top={top} outside={outside} reading={reading} /></span>
        </span>
        <Mini series={series} ceiling={ceiling} risk={risk} selUnits={selUnits} visible={under} onBack={back} />
      </div>
    <section
      ref={chartRef}
      aria-label="Agent work and the cases in it, over time"
      aria-busy={hourly.status === "loading" ? "true" : undefined}
      className="grid shrink-0 border-b border-[color:var(--line)] pb-[10px]"
      data-findings-time=""
    >
        <div ref={boxRef} className="relative min-w-0" style={{ height: LANE + AXIS_H, display: minimized ? "none" : undefined }}>
          {width ? (
            <>
              <svg
                ref={svgRef}
                width={width}
                height={LANE + AXIS_H}
                role="img"
                aria-label={`${words.label}: ${words.cases} cases`}
                className="block touch-none select-none"
                style={{ cursor: drag.current || atEdge ? "ew-resize" : top ? "pointer" : "crosshair" }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerLeave={onPointerLeave}
              >
                <defs>
                  <pattern id={`${ids}r`} width="4" height="3" patternUnits="userSpaceOnUse">
                    <rect width="4" height="1" style={{ fill: "var(--plot-ink-soft)" }} />
                  </pattern>
                  <pattern id={`${ids}u`} width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                    <rect width="1" height="4" style={{ fill: "var(--plot-ink-soft)" }} />
                  </pattern>
                  <clipPath id={`${ids}s`}><rect x={sx0} y={0} width={sx1 - sx0} height={WORK_H} /></clipPath>
                </defs>
                <rect x={sx0} y={0} width={sx1 - sx0} height={LANE} style={{ fill: "var(--green-soft)" }} />
                {unreadEnd > selUnits[0] ? <rect x={selUnits[0] * uw} y={WORK_H + 1} width={(unreadEnd - selUnits[0]) * uw} height={RISK_H - 1} fill={`url(#${ids}u)`} data-time-unread="" /> : null}
                {/* While the work and the cases are read, their lanes pulse. */}
                {hourly.status === "loading" ? <rect x={0} y={8} width={width} height={WORK_H - 8} className="animate-pulse motion-reduce:animate-none" style={{ fill: "var(--line-soft)", opacity: 0.5 }} /> : null}
                {reading ? <rect x={0} y={WORK_H + 1} width={width} height={RISK_H - 1} className="animate-pulse motion-reduce:animate-none" style={{ fill: "var(--line-soft)", opacity: 0.5 }} /> : null}
                {outline ? (
                  <g className="fade-in">
                    <g opacity={0.4}>
                      <path d={outline} fill={`url(#${ids}r)`} />
                      <path d={outline} style={{ fill: "none", stroke: "var(--muted-soft)", strokeOpacity: 0.55, strokeWidth: 1 }} />
                    </g>
                    <g clipPath={`url(#${ids}s)`}>
                      <path d={outline} fill={`url(#${ids}r)`} />
                      <path d={outline} style={{ fill: "none", stroke: "var(--muted-soft)", strokeOpacity: 0.55, strokeWidth: 1 }} />
                    </g>
                  </g>
                ) : null}
                {risk.map((unit, i) => {
                  const n = unit.cases.length;
                  if (!n) return null;
                  const h = Math.min(RISK_H - 1, Math.max(RISK_MIN, Math.round((n / mostCases) * (RISK_H - 1))));
                  let y = WORK_H + 1;
                  return (
                    <g key={i}>
                      {SEVERITIES.map((sev) => {
                        const k = unit.bySeverity[sev];
                        if (!k) return null;
                        const seg = Math.max(1, (h * k) / n);
                        y += seg;
                        return <rect key={sev} x={i * uw} y={y - seg} width={Math.max(1, uw)} height={seg} style={{ fill: SEVERITY[sev] }} />;
                      })}
                    </g>
                  );
                })}
                <line x1={0} x2={width} y1={WORK_H + 0.5} y2={WORK_H + 0.5} style={{ stroke: "var(--line-strong)" }} />
                {hover != null && !draft ? <line x1={hover * uw + uw / 2} x2={hover * uw + uw / 2} y1={0} y2={LANE} style={{ stroke: "var(--ink)", strokeOpacity: 0.5 }} /> : null}
                <g style={{ stroke: "var(--ink)", strokeWidth: 1.25, fill: "none" }}>
                  <path d={`M${sx0 + 0.5} ${corner} V0.5 H${sx0 + corner}`} />
                  <path d={`M${sx1 - corner} 0.5 H${sx1 - 0.5} V${corner}`} />
                  <path d={`M${sx0 + 0.5} ${LANE - corner} V${LANE - 0.5} H${sx0 + corner}`} />
                  <path d={`M${sx1 - corner} ${LANE - 0.5} H${sx1 - 0.5} V${LANE - corner}`} />
                </g>
                {labels.map(([d, i]) => (
                  <g key={d}>
                    <line x1={i * dayW + 0.5} x2={i * dayW + 0.5} y1={LANE} y2={LANE + 4} style={{ stroke: "var(--line-strong)" }} />
                    <text x={i * dayW + 3} y={LANE + 15} style={{ fill: "var(--muted-soft)", fontSize: 10.5 }}>{ON_DATE.format(d)}</text>
                  </g>
                ))}
                {isToday ? <text x={width - 1} y={LANE + 15} textAnchor="end" style={{ fill: "var(--muted-soft)", fontSize: 10.5 }}>Today</text> : null}
              </svg>
              {[["start", sx0, sel[0]], ["end", sx1, sel[1]]].map(([edge, x, i]) => (
                <div
                  key={edge}
                  role="slider"
                  tabIndex={0}
                  aria-label={edge === "start" ? "Range start" : "Range end"}
                  aria-valuemin={0}
                  aria-valuemax={days.length - 1}
                  aria-valuenow={i}
                  aria-valuetext={ON_DATE.format(days[i])}
                  onKeyDown={nudge(edge)}
                  className="pointer-events-none absolute top-0 w-[10px] -translate-x-1/2 rounded-[2px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--green)]"
                  // Kept inside the chart at its ends, so a range ending today does not widen the list.
                  style={{ left: clamp(x, 5, width - 5), height: LANE }}
                  data-time-edge={edge}
                />
              ))}
            </>
          ) : null}
        </div>
    </section>
    </>
  );
}
